import { useState } from 'react';
import { useOrderContext } from '../contexts/orderContext';
import { useReturnContext } from '../contexts/returnContext';

export type TextComponentConfiguration = {
  type: 'Text';
};

export type SelectComponentConfiguration = {
  type: 'Select';
  options: string[];
};

export type DatePickerComponentConfiguration = {
  type: 'DatePicker';
};

export type ToggleComponentConfiguration = {
  type: 'Toggle';
};

export type ReturnFormsResponse = {
  id: number;
  headerTranslationKey: string;
  descriptionTranslationKey: string;
  sortOrder: number;
  components: {
    id: number;
    required: boolean;
    labelTranslationKey: string;
    sortOrder: number;
    componentConfiguration:
      | TextComponentConfiguration
      | SelectComponentConfiguration
      | DatePickerComponentConfiguration
      | ToggleComponentConfiguration;
  }[];
};

export type ReturnFormsAnswer = {
  returnFormComponentId: number;
  salesOrderLineId: number;
  reason: string;
  answer: {
    type: string;
    value: any;
  };
};

export default function useReturnForms(orderLineId: number, reason?: string) {
  const { returnForms } = useOrderContext();
  const { returns } = useReturnContext();

  const getSavedFormsAnswers = (reason: string) => {
    const savedFormsAnswers = Object.values(returns).flatMap((returnItem) => returnItem.returnData.returnForms ?? []);

    return savedFormsAnswers.filter((answer) => answer.salesOrderLineId === orderLineId && answer.reason === reason)
      .reduce((object, savedAnswer) => ({ ...object, [savedAnswer.returnFormComponentId]: savedAnswer.answer }), {});
  };

  const [returnFormsAnswers] = useState<{
    [key: string]: ReturnFormsAnswer;
  }>(reason ? getSavedFormsAnswers(reason) : {});

  const sortedReturnForms = returnForms?.sort((returnForm1, returnForm2) => {
    return returnForm1.sortOrder - returnForm2.sortOrder;
  }) || [];

  const isAnswersValid = () => {
    const allComponents = returnForms?.flatMap((returnForm) => returnForm.components);
    const requiredComponents = allComponents.filter((component) => component.required);

    return requiredComponents.every((component) => !!returnFormsAnswers[component.id]);
  };

  return {
    answers: returnFormsAnswers,
    isAnswersValid,
    hasForms: returnForms && returnForms.length > 0,
    sortedReturnForms,
  };
}
